import {Component, computed, ElementRef, HostListener, inject, OnDestroy, OnInit, signal, WritableSignal} from '@angular/core';
import {Router} from '@angular/router';
import {CompanyService} from '@services/company.service';
import {CompanyRouteContextService} from '@services/company-route-context.service';
import {ICompanyDetailsSimplified} from '@models/company.interface';
import {ModalService} from '@services/frontend/modal.service';
import {CompanyAddDialog} from '@components/company/company-list/company-add-dialog/company-add-dialog';
import {CompanyRemoveDialog} from '@shared/components/sidebar/company-selector/company-remove-dialog';
import {NotificationService} from '@services/frontend/notification.service';
import {UserStateService} from '@services/frontend/user-state.service';
import {Role} from '@models/user.interface';
import {ExecutiveSubscriptionState} from '@services/executive-subscription.state';
import {FREE_EXECUTIVE_MAX_COMPANIES} from '@constants/executive-limits';
import {CompanyRefreshService} from '@services/company-refresh.service';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-company-selector',
  templateUrl: './company-selector.html',
  host: {class: 'block w-full px-3 py-2 border-b border-gray-100 shrink-0'},
})
export class CompanySelector implements OnInit, OnDestroy {
  private readonly companyService = inject(CompanyService);
  private readonly router = inject(Router);
  private readonly routeContext = inject(CompanyRouteContextService);
  private readonly modalService = inject(ModalService);
  private readonly notificationService = inject(NotificationService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly userStateService = inject(UserStateService);
  private readonly executiveSubscriptionState = inject(ExecutiveSubscriptionState);
  private readonly companyRefreshService = inject(CompanyRefreshService);

  companies: WritableSignal<ICompanyDetailsSimplified[]> = signal([]);
  menuOpen: WritableSignal<boolean> = signal(false);

  private refreshSubscription?: Subscription;

  proUpgradeRequired = computed(() => {
    if (this.userStateService.getUser()()?.role !== Role.EXECUTIVE) {
      return false;
    }
    if (!this.executiveSubscriptionState.loaded()) {
      return false;
    }
    return !this.executiveSubscriptionState.hasProSubscription()
      && this.companies().length >= FREE_EXECUTIVE_MAX_COMPANIES;
  });

  ngOnInit(): void {
    this.executiveSubscriptionState.ensureLoaded();
    this.refreshCompanies();
    this.refreshSubscription = this.companyRefreshService.onRefresh$.subscribe(() => {
      this.refreshCompanies();
    });
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }

  refreshCompanies(): void {
    this.companyService.getCompanies().subscribe({
      next: (list) => this.companies.set(list),
      error: () => this.companies.set([]),
    });
  }

  currentSiret(): string | null {
    return this.routeContext.currentSiret();
  }

  selectedCompany(): ICompanyDetailsSimplified | undefined {
    const s = this.currentSiret();
    if (!s) return undefined;
    return this.companies().find((c) => c.siret === s);
  }

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.menuOpen.update((v) => !v);
  }

  selectCompany(siret: string): void {
    this.menuOpen.set(false);
    void this.router.navigate(['/dashboard', 'company', siret, 'tableau-de-bord']);
  }

  removeCompany(company: ICompanyDetailsSimplified, event: MouseEvent): void {
    event.stopPropagation();
    this.menuOpen.set(false);

    this.companyService.getCompanyRemovalEligibility(company.siret).subscribe({
      next: (eligibility) => {
        const ref = this.modalService.open(CompanyRemoveDialog, {
          title: 'Confirmation',
          data: {companyName: company.nomEntreprise, eligibility}
        });

        ref.afterClosed().subscribe((confirmed) => {
          if (!confirmed) {
            return;
          }
          const current = this.currentSiret();
          this.companyService.removeCompanyForUser(company.siret).subscribe({
            next: () => {
              this.notificationService.success(`${company.nomEntreprise} supprimée`, 2500);
              this.refreshCompanies();
              if (current === company.siret) {
                void this.router.navigate(['/dashboard']);
              }
            },
            error: (err) => {
              const message = typeof err.error === 'string' && err.error.length > 0
                ? err.error
                : 'Impossible de supprimer cette entreprise';
              this.notificationService.error(message, 4000);
            },
          });
        });
      },
      error: () => this.notificationService.error('Impossible de vérifier la suppression', 3000),
    });
  }

  openAddCompany(): void {
    this.menuOpen.set(false);
    const ref = this.modalService.open(CompanyAddDialog, {
      title: 'Ajouter une nouvelle entreprise',
      data: {proUpgradeRequired: this.proUpgradeRequired()},
    });
    ref.afterClosed().subscribe(() => this.refreshCompanies());
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.menuOpen.set(false);
    }
  }
}
